import {Scope} from '@wix/app-settings-client';
import {IWidgetControllerConfig} from '@wix/native-components-infra';
import {I$WWrapper, IFedOpsLogger} from '@wix/native-components-infra/dist/es/src/types/types';
import {createSlotVeloAPIFactory} from '@wix/widget-plugins-ooi/velo';
import {APP_DEFINITION_ID, StoresWidgetID} from '@wix/wixstores-client-core/dist/es/src/constants';
import {withErrorReporting} from '@wix/wixstores-client-storefront-sdk/dist/es/src/viewer-script/errorReporter';
import {ProductPageWidgetViewState} from '@wix/wixstores-client-storefront-sdk/dist/es/src/enums/productPageWidgetViewState';
import {CreateControllerFn} from '@wix/yoshi-flow-editor';
import _ from 'lodash';
import {
  PRODUCT_PAGE_WIXCODE_APP_NAME,
  productPageFedopsEvent,
  productPagePanoramaEvent,
  UserInputType,
} from '../constants';
import {wixCodeGetProduct} from '../services/getProduct';
import {IProductPageControllerConfig} from '../types/app-types';
import {
  OnChoiceSelectedCallbackType,
  OnQuantityChangedCallbackType,
  OverrideCallback,
  ProductPageStore,
} from './ProductPageStore';
import {ViewerContext} from '@wix/wixstores-client-storefront-common';

type ResolveFn<T> = (value: T | PromiseLike<T>) => void;
export type ReportError = (e: string | Error, shouldRethrowException?: boolean) => any;

export const ProductPageController: CreateControllerFn = (controllerParams) => {
  const {controllerConfig, flowAPI} = controllerParams;
  flowAPI.panoramaClient.transaction(productPagePanoramaEvent.ProductPageLoaded).start();
  const {setProps} = controllerConfig;
  const siteStore = controllerParams.appData.context.siteStore;
  let onReadyResolver: ResolveFn<void>;

  let productPageStore: ProductPageStore;
  let config: IProductPageControllerConfig = _.clone(controllerConfig.config);

  const fedopsLoggerFactory = siteStore.platformServices.fedOpsLoggerFactory;
  const fedopsLogger: IFedOpsLogger = fedopsLoggerFactory.getLoggerForWidget({
    appId: APP_DEFINITION_ID,
    widgetId: StoresWidgetID.PRODUCT_PAGE,
    appName: PRODUCT_PAGE_WIXCODE_APP_NAME,
  });

  const waitUntilReady = new Promise<void>((resolve) => {
    onReadyResolver = resolve;
  });

  const controllerConfigRef: IWidgetControllerConfig = controllerConfig;

  const exports = () =>
    withErrorReporting(flowAPI.reportError)({
      getProduct: async () => {
        await waitUntilReady;
        const panoramaInteraction = productPagePanoramaEvent.WixCodeGetProduct;
        flowAPI.panoramaClient.transaction(panoramaInteraction).start();
        const onSuccess = () => {
          flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        };
        return wixCodeGetProduct(siteStore, flowAPI.reportError, onSuccess);
      },
      // @deprecated - New method is getCustomText
      getCustomTextFieldsValues: (): string[] => {
        const panoramaInteraction = productPagePanoramaEvent.WixCodeGetCustomTextFieldValues;
        flowAPI.panoramaClient.transaction(panoramaInteraction).start();
        const customTextFieldsValues = productPageStore.userInputs.text;
        flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        return customTextFieldsValues;
      },
      // eslint-disable-next-line sonarjs/no-identical-functions
      getCustomText: async (): Promise<string[]> => {
        await waitUntilReady;
        const panoramaInteraction = productPagePanoramaEvent.WixCodeGetCustomText;
        flowAPI.panoramaClient.transaction(panoramaInteraction).start();
        const customTextFieldsValues = productPageStore.customText;
        flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        return customTextFieldsValues;
      },
      getProductId: async () => {
        await waitUntilReady;
        const panoramaInteraction = productPagePanoramaEvent.WixCodeGetProductId;
        const productId = productPageStore.productId;
        flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        return productId;
      },
      getQuantity: async () => {
        await waitUntilReady;
        const panoramaInteraction = productPagePanoramaEvent.WixCodeGetQuantity;
        const selectedQuantity = productPageStore.selectedQuantity;
        flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        return selectedQuantity;
      },
      setQuantity: async (value: number) => {
        await waitUntilReady;
        const panoramaInteraction = productPagePanoramaEvent.WixCodeSetQuantity;
        productPageStore.handleUserInput(UserInputType.Quantity, value, 0);
        flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
      },
      onQuantityChanged: async (callback: OnQuantityChangedCallbackType) => {
        await waitUntilReady;
        const panoramaInteraction = productPagePanoramaEvent.WixCodeOnQuantityChanged;
        productPageStore.setOnQuantityChangedCallback(callback);
        flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
      },
      getSelectedVariantId: async () => {
        await waitUntilReady;
        const panoramaInteraction = productPagePanoramaEvent.WixCodeGetSelectedVariantId;
        const selectedVariantId = productPageStore.selectedVariant?.id;
        flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        return selectedVariantId;
      },
      getSelectedChoices: async () => {
        await waitUntilReady;
        const panoramaInteraction = productPagePanoramaEvent.WixCodeGetSelectedChoices;
        const selectedChoices = productPageStore.selectedChoices;
        flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        return selectedChoices;
      },
      onChoiceSelected: async (callback: OnChoiceSelectedCallbackType) => {
        await waitUntilReady;
        const panoramaInteraction = productPagePanoramaEvent.WixCodeOnChoiceSelected;
        productPageStore.setOnChoiceSelectedCallback(callback);
        flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
      },
      onAddToCart: async (callback: OverrideCallback) => {
        await waitUntilReady;
        const fedopsInteraction = productPageFedopsEvent.WixCodeOnAddToCart;
        const panoramaInteraction = productPagePanoramaEvent.WixCodeOnAddToCart;
        fedopsLogger.interactionStarted(fedopsInteraction);
        productPageStore.setOnAddToCartCallback(callback);
        flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        fedopsLogger.interactionEnded(fedopsInteraction);
      },
      setAddToCartLabel: async (text: string) => {
        await waitUntilReady;
        const fedopsInteraction = productPageFedopsEvent.WixCodeOnAddToCart;
        const panoramaInteraction = productPagePanoramaEvent.WixCodeOnAddToCart;
        fedopsLogger.interactionStarted(fedopsInteraction);
        productPageStore.setAddToCartTextOverride(text);
        flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        fedopsLogger.interactionEnded(fedopsInteraction);
      },
      onBuyNow: async (callback: OverrideCallback) => {
        await waitUntilReady;
        const fedopsInteraction = productPageFedopsEvent.WixCodeOnBuyNow;
        const panoramaInteraction = productPagePanoramaEvent.WixCodeOnBuyNow;
        fedopsLogger.interactionStarted(fedopsInteraction);
        productPageStore.setOnBuyNowCallback(callback);
        flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        fedopsLogger.interactionEnded(fedopsInteraction);
      },
      setBuyNowLabel: async (text: string) => {
        await waitUntilReady;
        const fedopsInteraction = productPageFedopsEvent.WixCodeOnBuyNow;
        const panoramaInteraction = productPagePanoramaEvent.WixCodeOnBuyNow;
        fedopsLogger.interactionStarted(fedopsInteraction);
        productPageStore.setBuyNowTextOverride(text);
        flowAPI.panoramaClient.transaction(panoramaInteraction).finish();
        fedopsLogger.interactionEnded(fedopsInteraction);
      },
    });

  const updateConfig = (_$w: I$WWrapper, newConfig: IProductPageControllerConfig) => {
    config = _.clone(newConfig);
    return productPageStore.updateState(config, newConfig.publicData);
  };

  const updateAppSettings = (_$w: I$WWrapper, updates: {[key: string]: any}) => {
    /* istanbul ignore else */
    if (updates.scope === Scope.COMPONENT && updates.source === 'app-settings') {
      productPageStore.updateAppSettings(updates.payload);
    }
  };

  const updateWidgetViewState = (viewState: ProductPageWidgetViewState) => {
    productPageStore.updateWidgetViewState(viewState);
  };

  const load = async () => {
    const {publicData} = config;
    if (productPageStore) {
      return;
    }

    productPageStore = new ProductPageStore(
      config,
      controllerParams.dangerousPublicDataOverride,
      publicData,
      setProps,
      siteStore,
      viewerContext,
      controllerConfigRef.config.externalId || '',
      flowAPI.reportError,
      controllerConfigRef.compId,
      createSlotVeloAPIFactory(controllerConfigRef),
      controllerConfigRef,
      flowAPI.errorHandler,
      flowAPI
    );

    await productPageStore
      .setInitialState()
      .then(() => {
        onReadyResolver();
      })
      .catch(flowAPI.reportError);
  };

  const viewerContext = new ViewerContext(siteStore, flowAPI.experiments, controllerConfig.wixCodeApi, flowAPI);

  const init = async () => {
    await viewerContext.loadConfig(true);
    controllerParams.controllerConfig.wixCodeApi.location.onChange(() => void load());
    await load();
  };

  return {
    async pageReady() {
      await init();
    },
    load,
    exports,
    updateConfig,
    updateAppSettings,
    updateWidgetViewState,
  };
};
